import { useCallback, useEffect, useState } from 'react'
import type {
  InboxItem,
  InboxItemKind,
  InboxItemState,
  InboxListResponse,
  SpreadsheetPreview,
} from '@/types/inbox'
import type { InvoiceInput } from '@/types/database'

const API_URL = import.meta.env.VITE_API_URL || '/v1'

async function apiCall<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options?.headers },
  })

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(body.error || body.message || 'Request failed')
  }

  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}

/** URL of the original document, embeddable when inline is requested. */
export function documentUrl(documentId: string, inline = false): string {
  return `${API_URL}/documents/${documentId}/file${inline ? '?inline=1' : ''}`
}

export function useInbox(initialState: InboxItemState | 'all' = 'new') {
  const [items, setItems] = useState<InboxItem[]>([])
  const [pending, setPending] = useState(0)
  const [stateFilter, setStateFilter] = useState<InboxItemState | 'all'>(initialState)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await apiCall<InboxListResponse>(`/inbox?state=${stateFilter}`)
      setItems(data.items ?? [])
      setPending(data.pending ?? 0)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Inbox konnte nicht geladen werden')
    } finally {
      setLoading(false)
    }
  }, [stateFilter])

  useEffect(() => {
    void refresh()
  }, [refresh])

  /** Take the invoice into the database — the "invoice is fine" path. */
  const accept = useCallback(
    async (id: string, overrides?: Partial<InvoiceInput>) => {
      await apiCall(`/inbox/${id}/accept`, {
        method: 'POST',
        body: JSON.stringify(overrides ?? {}),
      })
      await refresh()
    },
    [refresh],
  )

  /** Hand it to Flixpector for review and negotiation with the law firm. */
  const sendToFlixpector = useCallback(
    async (id: string) => {
      await apiCall(`/inbox/${id}/flixpector`, { method: 'POST' })
      await refresh()
    },
    [refresh],
  )

  const remove = useCallback(
    async (id: string) => {
      await apiCall(`/inbox/${id}`, { method: 'DELETE' })
      await refresh()
    },
    [refresh],
  )

  /** Acknowledge without deleting — for a "no invoices this month" notice. */
  const dismiss = useCallback(
    async (id: string) => {
      await apiCall(`/inbox/${id}/dismiss`, { method: 'POST' })
      await refresh()
    },
    [refresh],
  )

  const changeKind = useCallback(
    async (id: string, kind: InboxItemKind) => {
      await apiCall(`/inbox/${id}/kind`, { method: 'PATCH', body: JSON.stringify({ kind }) })
      await refresh()
    },
    [refresh],
  )

  /**
   * Pull the current review state from Flixpector. reopen=true also puts the
   * item back in front of the user, which is what the send button in Flixpector
   * triggers on its side.
   */
  const syncFlixpector = useCallback(
    async (id: string, reopen = false) => {
      await apiCall(`/inbox/${id}/sync-flixpector${reopen ? '?reopen=1' : ''}`, { method: 'POST' })
      await refresh()
    },
    [refresh],
  )

  const loadPreview = useCallback(
    (id: string) => apiCall<SpreadsheetPreview>(`/inbox/${id}/preview`),
    [],
  )

  return {
    items,
    pending,
    stateFilter,
    setStateFilter,
    loading,
    error,
    refresh,
    accept,
    sendToFlixpector,
    remove,
    dismiss,
    changeKind,
    syncFlixpector,
    loadPreview,
  }
}

/**
 * Just the pending count, for the nav badge. Polls slowly — mail is fetched
 * every 30 minutes, so anything faster is wasted traffic.
 */
export function useInboxCount(pollMs = 120_000) {
  const [pending, setPending] = useState(0)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      try {
        const data = await apiCall<{ pending: number }>('/inbox/count')
        if (!cancelled) setPending(data.pending ?? 0)
      } catch {
        // A failing badge must never break the header
      }
    }

    void load()
    const timer = setInterval(() => void load(), pollMs)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [pollMs])

  return pending
}
